export const RESOURCE_CATEGORIES = ["image", "photoshop", "vector", "other"];
export const RESOURCE_STATUSES = ["draft", "published", "deleted"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const date = (value) => typeof value === "string" && Number.isFinite(Date.parse(value));

function text(value, max, field, required = false) {
  if (typeof value !== "string" || value.length > max || (required && !value.trim()))
    throw new Error(`Invalid resource ${field}`);
  return value.trim();
}

export function validateResourceUrl(value, { optional = false } = {}) {
  const source = text(value, 2048, "URL");
  if (!source && optional) return "";
  let url;
  try { url = new URL(source); } catch { throw new Error("Resource URL must be a public HTTPS URL"); }
  const hostname = url.hostname.toLowerCase();
  if (url.protocol !== "https:" || url.username || url.password || !hostname.includes(".") ||
    /(?:^|\.)(?:localhost|local|internal)$/.test(hostname) || hostname.endsWith(".") ||
    hostname.startsWith("[") || /^\d+(?:\.\d+){3}$/.test(hostname))
    throw new Error("Resource URL must be a public HTTPS URL");
  return url.href;
}

export function normalizeResourceDownloadUrl(value, options) {
  const normalized = validateResourceUrl(value, options);
  if (!normalized) return "";
  const url = new URL(normalized);
  if (["dropbox.com", "www.dropbox.com"].includes(url.hostname) && /^\/(?:s\/|scl\/fi\/)/.test(url.pathname)) {
    url.searchParams.delete("raw");
    url.searchParams.set("dl", "1");
  }
  return url.href;
}

export function validateResourceInput(value) {
  if (!object(value) || !RESOURCE_CATEGORIES.includes(value.category) || !["draft", "published"].includes(value.status))
    throw new Error("Invalid resource category or status");
  const optional = value.status === "draft";
  return {
    title: text(value.title, 140, "title", true),
    note: text(value.note, 2000, "note"),
    thumbnailUrl: validateResourceUrl(value.thumbnailUrl, { optional }),
    downloadUrl: normalizeResourceDownloadUrl(value.downloadUrl, { optional }),
    category: value.category,
    status: value.status,
  };
}

export function validateManagedResource(value) {
  if (!object(value) || !UUID.test(value.id) || !RESOURCE_STATUSES.includes(value.status) ||
    !Number.isSafeInteger(value.version) || value.version < 1 || !date(value.createdAt) || !date(value.updatedAt))
    throw new Error("Invalid resource record");
  validateResourceInput({ ...value, status: value.status === "deleted" ? "draft" : value.status });
  return value;
}

export function validateResourceList(value, { publicOnly = false } = {}) {
  if (!object(value) || !Array.isArray(value.items) || value.items.length > 500 ||
    !Number.isSafeInteger(value.total) || value.total < value.items.length ||
    !Number.isSafeInteger(value.limit) || value.limit < 1 || value.limit > 500 || value.items.length > value.limit)
    throw new Error("Invalid resource list");
  for (const item of value.items) {
    validateManagedResource(item);
    if (publicOnly && item.status !== "published") throw new Error("Unpublished public resource");
  }
  if (new Set(value.items.map(item => item.id)).size !== value.items.length) throw new Error("Duplicate resource records");
  return value;
}
